import { captureMessage } from "@sentry/react"
import { getToolName } from "ai"

import {
    ASSISTANT_TOOL_NAMES,
    ASSISTANT_TOOL_OUTPUT_STATUSES,
    ASSISTANT_TOOL_OUTPUT_TYPES,
} from "~components/outfitter-assistant/assistant-tool-constants"
import {
    getOutputRecord,
    isRecord,
    type AnyToolUIPart,
} from "~components/shared/chat/tool-ui-part-helpers"
import COPY from "~config/copy-constants"
import { MessageTemplateChannel } from "~graphql/generated/types"
import { parseOutfitterFormBuilderDraft } from "~components/forms/outfitter-form-builder-draft-helpers"
import {
    ASSISTANT_EMAIL_TEMPLATE_EDIT_STATE_KEY,
    pendingEmailTemplateAssistantEditVar,
    type PendingEmailTemplateAssistantEdit,
} from "~utils/assistant-pending-email-template-edit-var"
import {
    ASSISTANT_NEW_OUTFITTER_FORM_ID,
    ASSISTANT_OUTFITTER_FORM_DRAFT_STATE_KEY,
    pendingOutfitterFormAssistantDraftVar,
    type PendingOutfitterFormAssistantDraft,
} from "~utils/assistant-pending-outfitter-form-draft-var"
import {
    isEditEmailTemplatePathname,
    isEditFormPathname,
    isNewFormPathname,
    navigateToEditEmailTemplate,
    navigateToEditForm,
    navigateToEditSmsTemplate,
    navigateToNewForm,
    navigateToRoute,
} from "~utils/helpers/navigation-helpers"
import { deliverPendingAssistantEdit } from "~utils/assistant-edit-handoff"
import { outfitterAssistantPathnameVar } from "~utils/outfitter-assistant-pathname-var"

const TOOL_OUTPUT_AVAILABLE_STATE = "output-available"

type AssistantClientToolContext = {
    readonly part: AnyToolUIPart
    readonly executedToolCallIds: Set<string>
}

type AssistantClientToolHandler = (ctx: AssistantClientToolContext) => void

const ASSISTANT_CLIENT_TOOL_HANDLERS: Readonly<
    Record<string, AssistantClientToolHandler>
> = {
    [ASSISTANT_TOOL_NAMES.NAVIGATE_TO_PAGE]: handleNavigateToPageTool,
    [ASSISTANT_TOOL_NAMES.CREATE_MESSAGE_TEMPLATE]:
        handleCreateMessageTemplateTool,
    [ASSISTANT_TOOL_NAMES.EDIT_EMAIL_TEMPLATE]: handleEditEmailTemplateTool,
    [ASSISTANT_TOOL_NAMES.PREPARE_OUTFITTER_FORM]:
        handlePrepareOutfitterFormTool,
}

function getAssistantClientToolHandler(
    toolName: string
): AssistantClientToolHandler | undefined {
    return ASSISTANT_CLIENT_TOOL_HANDLERS[toolName]
}

function toolOutputRequiresClientExecution(
    output: Readonly<Record<string, unknown>> | null
): boolean {
    if (!output) {
        return false
    }

    return (
        (output.type === ASSISTANT_TOOL_OUTPUT_TYPES.NAVIGATION &&
            output.status === ASSISTANT_TOOL_OUTPUT_STATUSES.SUCCESS) ||
        (output.type === ASSISTANT_TOOL_OUTPUT_TYPES.MESSAGE_TEMPLATE_CREATE &&
            output.status ===
                ASSISTANT_TOOL_OUTPUT_STATUSES.NEEDS_CLIENT_EXECUTION) ||
        (output.type === ASSISTANT_TOOL_OUTPUT_TYPES.EMAIL_TEMPLATE_EDIT &&
            output.status ===
                ASSISTANT_TOOL_OUTPUT_STATUSES.NEEDS_CLIENT_EXECUTION) ||
        (output.type === ASSISTANT_TOOL_OUTPUT_TYPES.OUTFITTER_FORM_PREPARE &&
            output.status ===
                ASSISTANT_TOOL_OUTPUT_STATUSES.NEEDS_CLIENT_EXECUTION)
    )
}

function logMissingAssistantClientTool({
    toolName,
    toolCallId,
}: {
    readonly toolName: string
    readonly toolCallId: string
}): void {
    const message = `${COPY.AI_CHAT_TOOL_CLIENT_NOT_IMPLEMENTED}: ${toolName} (${toolCallId})`

    void captureMessage(message, "warning")
}

function logInvalidOutfitterFormDraft({
    toolCallId,
}: {
    readonly toolCallId: string
}): void {
    const message = `${COPY.AI_CHAT_TOOL_OUTFITTER_FORM_PREPARE_INVALID} (${toolCallId})`

    void captureMessage(message, "warning")
}

export function executeAssistantClientToolPartIfNeeded({
    part,
    executedToolCallIds,
}: AssistantClientToolContext): void {
    if (executedToolCallIds.has(part.toolCallId)) {
        return
    }
    if (part.state !== TOOL_OUTPUT_AVAILABLE_STATE) {
        return
    }

    const toolName = getToolName(part)
    const output = getOutputRecord(part)

    if (!toolOutputRequiresClientExecution(output)) {
        return
    }

    const handler = getAssistantClientToolHandler(toolName)

    if (!handler) {
        logMissingAssistantClientTool({ toolName, toolCallId: part.toolCallId })
        executedToolCallIds.add(part.toolCallId)
        return
    }

    handler({ part, executedToolCallIds })
}

function handleNavigateToPageTool({
    part,
    executedToolCallIds,
}: AssistantClientToolContext): void {
    const resolvedPath = getReadyNavigationPath(part)
    if (!resolvedPath) {
        return
    }

    executedToolCallIds.add(part.toolCallId)
    void navigateToRoute(resolvedPath)
}

function handleCreateMessageTemplateTool({
    part,
    executedToolCallIds,
}: AssistantClientToolContext): void {
    const output = getOutputRecord(part)
    if (!output) {
        return
    }

    if (output.type !== ASSISTANT_TOOL_OUTPUT_TYPES.MESSAGE_TEMPLATE_CREATE) {
        return
    }
    if (
        output.status !== ASSISTANT_TOOL_OUTPUT_STATUSES.NEEDS_CLIENT_EXECUTION
    ) {
        return
    }

    const templateId =
        typeof output.templateId === "string" ? output.templateId : ""
    if (!templateId) {
        return
    }

    executedToolCallIds.add(part.toolCallId)

    // SMS content is saved server-side and read back by the SMS edit form, so a
    // plain navigation is enough. Email content only reaches the GrapesJS editor
    // through the same live-edit handoff EDIT_EMAIL_TEMPLATE uses.
    if (output.channel === MessageTemplateChannel.Sms) {
        void navigateToEditSmsTemplate(templateId)
        return
    }

    deliverEmailTemplateAssistantEdit({ output, toolCallId: part.toolCallId })
}

function handleEditEmailTemplateTool({
    part,
    executedToolCallIds,
}: AssistantClientToolContext): void {
    const output = getOutputRecord(part)
    if (!output) {
        return
    }

    if (output.type !== ASSISTANT_TOOL_OUTPUT_TYPES.EMAIL_TEMPLATE_EDIT) {
        return
    }
    if (
        output.status !== ASSISTANT_TOOL_OUTPUT_STATUSES.NEEDS_CLIENT_EXECUTION
    ) {
        return
    }

    if (
        deliverEmailTemplateAssistantEdit({
            output,
            toolCallId: part.toolCallId,
        })
    ) {
        executedToolCallIds.add(part.toolCallId)
    }
}

// Hands assistant-drafted email content (subject/html/css) to the live template
// editor: stashed in the reactive var when the editor is already open, otherwise
// carried through navigation state. Shared by create and edit flows.
function deliverEmailTemplateAssistantEdit({
    output,
    toolCallId,
}: {
    output: Readonly<Record<string, unknown>>
    toolCallId: string
}): boolean {
    const templateId =
        typeof output.templateId === "string" ? output.templateId : ""
    if (!templateId) {
        return false
    }

    const payload: PendingEmailTemplateAssistantEdit = {
        templateId,
        toolCallId,
        subject:
            typeof output.subject === "string" ? output.subject : undefined,
        html: typeof output.html === "string" ? output.html : undefined,
        css: typeof output.css === "string" ? output.css : undefined,
    }

    deliverPendingAssistantEdit({
        payload,
        stateKey: ASSISTANT_EMAIL_TEMPLATE_EDIT_STATE_KEY,
        isEditorPageOpen: isEditEmailTemplatePathname({
            pathname: outfitterAssistantPathnameVar(),
            templateId,
        }),
        stashForOpenEditor: pendingEmailTemplateAssistantEditVar,
        navigateToEditorPage: (state) =>
            void navigateToEditEmailTemplate(templateId, { state }),
    })

    return true
}

function handlePrepareOutfitterFormTool({
    part,
    executedToolCallIds,
}: AssistantClientToolContext): void {
    const output = getOutputRecord(part)
    if (!output) {
        return
    }

    if (output.type !== ASSISTANT_TOOL_OUTPUT_TYPES.OUTFITTER_FORM_PREPARE) {
        return
    }
    if (
        output.status !== ASSISTANT_TOOL_OUTPUT_STATUSES.NEEDS_CLIENT_EXECUTION
    ) {
        return
    }

    const formId =
        typeof output.formId === "string" && output.formId
            ? output.formId
            : ASSISTANT_NEW_OUTFITTER_FORM_ID
    const draft = parseOutfitterFormBuilderDraft(output.draft)
    if (!draft) {
        logInvalidOutfitterFormDraft({ toolCallId: part.toolCallId })
        executedToolCallIds.add(part.toolCallId)
        return
    }

    executedToolCallIds.add(part.toolCallId)

    applyOutfitterFormPrepareToBuilder({
        payload: { formId, toolCallId: part.toolCallId, draft },
        formId,
    })
}

function applyOutfitterFormPrepareToBuilder({
    payload,
    formId,
}: {
    payload: PendingOutfitterFormAssistantDraft
    formId: string | null
}) {
    const pathname = outfitterAssistantPathnameVar()

    deliverPendingAssistantEdit({
        payload,
        stateKey: ASSISTANT_OUTFITTER_FORM_DRAFT_STATE_KEY,
        isEditorPageOpen: formId
            ? isEditFormPathname({ pathname, formId })
            : isNewFormPathname(pathname),
        stashForOpenEditor: pendingOutfitterFormAssistantDraftVar,
        navigateToEditorPage: (state) => {
            if (formId) {
                void navigateToEditForm({ formId, options: { state } })
                return
            }
            void navigateToNewForm({ state })
        },
    })
}

function getReadyNavigationPath(part: AnyToolUIPart): string | null {
    if (getToolName(part) !== ASSISTANT_TOOL_NAMES.NAVIGATE_TO_PAGE) {
        return null
    }
    if (part.state !== TOOL_OUTPUT_AVAILABLE_STATE) {
        return null
    }

    const output = getOutputRecord(part)
    if (!isRecord(output)) {
        return null
    }
    if (output.type !== ASSISTANT_TOOL_OUTPUT_TYPES.NAVIGATION) {
        return null
    }
    if (output.status !== ASSISTANT_TOOL_OUTPUT_STATUSES.SUCCESS) {
        return null
    }

    const { resolvedPath } = output
    return typeof resolvedPath === "string" && resolvedPath
        ? resolvedPath
        : null
}
